import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'react-router-dom';
import { PiClockLight, PiXLight } from 'react-icons/pi';
import { RootState, useAppDispatch } from '../store';
import { isMatterEconomicsEnabled } from '../utils/config';
import {
  fetchActivityTypes,
  fetchMyTimeEntries,
  TimeEntryFilters,
} from '../store/actions/economicsActions';
import { showSidePanel, hideSidePanel } from '../store/actions/sidePanelActions';
import Table from '../components/shared/TailwindComponents/Table';
import Dropdown from '../components/shared/TailwindComponents/Dropdown';
import DateTimePicker from '../components/shared/TailwindComponents/DatePicker';
import Badge from '../components/shared/TailwindComponents/Badge';
import Button from '../components/shared/TailwindComponents/Button';
import Header from '../components/shared/TailwindComponents/Header';
import { ymdToDate, dateToYmd } from '../components/Matters/economics/dateHelpers';
import UserCell from '../components/Matters/economics/UserCell';
import { sortKeyOf, sortRowsBy } from '../components/Matters/economics/timesheetSort';
import type { ActivityType, MyTimeEntry } from '../types/economics';

/**
 * Personal timesheet: the current user's time entries across all accessible
 * matters (US #2237, task #2268). Server filters by date/activity/billable; the
 * matter filter is applied client-side over the loaded set.
 *
 * Wave 6 (time-1/2/3): the filter band is one control row of the same weight as
 * every other list page; every column sorts client-side over the loaded set;
 * and a row opens the entry's own side panel for reading and correcting it.
 */
const MyTimeEntriesPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const userId = userInfo?.UserID;

  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [entries, setEntries] = useState<MyTimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState<TimeEntryFilters>({});
  const [matterFilter, setMatterFilter] = useState<string>('');

  const activityLabel = useMemo(() => {
    const byId = new Map<number, string>();
    activityTypes.forEach((a) => byId.set(a.id, a.label));
    return (id?: number | null) => (id != null && byId.has(id) ? t(byId.get(id)!) : '—');
  }, [activityTypes, t]);

  useEffect(() => {
    dispatch(fetchActivityTypes()).then(setActivityTypes);
  }, [dispatch]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(false);
    try {
      const data = await dispatch(fetchMyTimeEntries(userId, filters));
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dispatch, userId, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Matter options derived from the loaded entries (client-side filter).
  const matterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    entries.forEach((e) => {
      if (e.matter_uuid && !seen.has(e.matter_uuid)) {
        seen.set(e.matter_uuid, e.matter_title || e.matter_uuid);
      }
    });
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [entries]);

  /* ─── Columns and sorting (time-2) ──────────────────────────────────────
     Keyed through `sortKeyOf` so the comparators match whatever `Table` sends
     back in any language, and reading the raw entry, never the rendered cell
     (see `timesheetSort.ts`). */

  const COLUMN_DEFS = useMemo(
    () => [
      { label: t('Date'), sort: (e: MyTimeEntry) => e.entry_date || '' },
      { label: t('Matter'), sort: (e: MyTimeEntry) => (e.matter_title || '').toLowerCase() },
      {
        label: t('Logged by'),
        sort: (e: MyTimeEntry) =>
          String(e.name || (e as any).user_name || e.email || e.user_id || '').toLowerCase(),
      },
      { label: t('Activity'), sort: (e: MyTimeEntry) => activityLabel(e.activity_type_id).toLowerCase() },
      // Hours is a decimal STRING on the wire: sorted as text, 9.5 lands after 10.
      { label: t('Hours'), sort: (e: MyTimeEntry) => Number(e.hours_decimal || 0) },
      { label: t('Billable'), sort: (e: MyTimeEntry) => (e.billable ? 1 : 0) },
      { label: t('Description'), sort: (e: MyTimeEntry) => (e.description || '').toLowerCase() },
    ],
    [t, activityLabel],
  );

  const columns = useMemo(() => COLUMN_DEFS.map((c) => c.label), [COLUMN_DEFS]);
  const sortableColumns = columns;

  const defaultSortBy = sortKeyOf(t('Date'));
  const [sortBy, setSortBy] = useState<string>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // A timesheet reads newest-first; a language switch changes the key, not the column.
  useEffect(() => {
    setSortBy((prev) => {
      const stillValid = COLUMN_DEFS.some((c) => sortKeyOf(c.label) === prev);
      return stillValid ? prev : sortKeyOf(t('Date'));
    });
  }, [COLUMN_DEFS, t]);

  const visible = useMemo(() => {
    const filtered = matterFilter ? entries.filter((e) => e.matter_uuid === matterFilter) : entries;
    const def = COLUMN_DEFS.find((c) => sortKeyOf(c.label) === sortBy);
    if (!def) return filtered;
    return sortRowsBy(filtered, def.sort, sortOrder);
  }, [entries, matterFilter, COLUMN_DEFS, sortBy, sortOrder]);

  const totalHours = visible.reduce((s, e) => s + Number(e.hours_decimal || 0), 0);
  const billableHours = visible.filter((e) => e.billable).reduce((s, e) => s + Number(e.hours_decimal || 0), 0);

  const rows = visible.map((e) => ({
    id: e.id,
    entry: e,
    [t('Date')]: e.entry_date,
    [t('Matter')]: e,
    [t('Logged by')]: e,
    [t('Activity')]: activityLabel(e.activity_type_id),
    [t('Hours')]: Number(e.hours_decimal).toFixed(2),
    [t('Billable')]: e.billable,
    [t('Description')]: e.description || '—',
  }));

  const renderCell = (column: string, value: any) => {
    if (column === t('Matter')) {
      const e = value as MyTimeEntry;
      if (e.matter_uuid) {
        return (
          <Link
            className="text-primary-600 hover:underline"
            to={`/matters/${e.matter_uuid}?tab=Time`}
            onClick={(ev) => ev.stopPropagation()}
          >
            {e.matter_title || e.matter_uuid}
          </Link>
        );
      }
      return e.matter_title || '—';
    }
    if (column === t('Logged by')) {
      return <UserCell user={value as MyTimeEntry} fallbackLabel={`User ${(value as MyTimeEntry).user_id}`} />;
    }
    if (column === t('Billable')) {
      return value
        ? <Badge color="green">{t('Billable')}</Badge>
        : <Badge color="gray">{t('Non-billable')}</Badge>;
    }
    return undefined;
  };

  /* ─── The entry's own surface (time-3) ───────────────────────────────────
     The same panel the matter Time tab opens — an economics time entry is one
     record whichever list you reached it from. Delete stays with the matter
     list, which owns that confirmation, so this one opens read + edit only. */
  const openEntryPanel = (row: any) => {
    const entry: MyTimeEntry | undefined = row?.entry;
    if (!entry) return;
    dispatch(
      showSidePanel({
        type: 'matterTimeEntry',
        showSidePanel: true,
        size: 'sm',
        message: t('Time entry'),
        icon: <PiClockLight className="w-6 h-6 text-gray-600" />,
        entry,
        matterUuid: entry.matter_uuid || '',
        activityTypes,
        fallbackLabel: `User ${entry.user_id}`,
        // Own entries only, and only where the row carries the matter it
        // belongs to — the write is `PATCH /matters/<uuid>/time-entries/<id>`.
        canManage: Boolean(entry.matter_uuid) && String(entry.user_id) === String(userId),
        allowDelete: false,
        onSaved: load,
        onDeleteEntry: () => undefined,
        onCancel: () => dispatch(hideSidePanel()),
      }),
    );
  };

  /* ─── Filters (time-1) ───────────────────────────────────────────────── */

  const activeFilters = useMemo(() => {
    const chips: Array<{ key: string; label: string; clear: () => void }> = [];
    if (filters.date_from) {
      chips.push({
        key: 'from',
        label: `${t('From')}: ${filters.date_from}`,
        clear: () => setFilters((p) => ({ ...p, date_from: undefined })),
      });
    }
    if (filters.date_to) {
      chips.push({
        key: 'to',
        label: `${t('To')}: ${filters.date_to}`,
        clear: () => setFilters((p) => ({ ...p, date_to: undefined })),
      });
    }
    if (matterFilter) {
      chips.push({
        key: 'matter',
        label: `${t('Matter')}: ${matterOptions.find((m) => m.value === matterFilter)?.label || matterFilter}`,
        clear: () => setMatterFilter(''),
      });
    }
    if (filters.activity_type_id) {
      const found = activityTypes.find((a) => String(a.id) === String(filters.activity_type_id));
      chips.push({
        key: 'activity',
        label: `${t('Activity')}: ${found ? t(found.label) : filters.activity_type_id}`,
        clear: () => setFilters((p) => ({ ...p, activity_type_id: undefined })),
      });
    }
    if (filters.billable !== undefined) {
      chips.push({
        key: 'billable',
        label: filters.billable ? t('Billable') : t('Non-billable'),
        clear: () => setFilters((p) => ({ ...p, billable: undefined })),
      });
    }
    return chips;
  }, [filters, matterFilter, matterOptions, activityTypes, t]);

  const clearAllFilters = () => {
    setFilters({});
    setMatterFilter('');
  };

  if (!isMatterEconomicsEnabled()) return <Redirect to="/home" />;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <Header breadcrumbs={[{ name: t('My time entries'), href: '/my-time', current: true }]} />

      {/* One control row, the same height and weight as every other list page. */}
      <div className="glass-bar flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-2">
        <div className="w-40">
          <DateTimePicker
            id="mytime-from"
            selected={ymdToDate(filters.date_from)}
            placeholder={t('From')}
            showTimeSelect={false}
            dateFormat="yyyy-MM-dd"
            thinner
            fullWidth
            onChange={(d) => setFilters((p) => ({ ...p, date_from: dateToYmd(d) || undefined }))}
          />
        </div>
        <div className="w-40">
          <DateTimePicker
            id="mytime-to"
            selected={ymdToDate(filters.date_to)}
            placeholder={t('To')}
            showTimeSelect={false}
            dateFormat="yyyy-MM-dd"
            thinner
            fullWidth
            onChange={(d) => setFilters((p) => ({ ...p, date_to: dateToYmd(d) || undefined }))}
          />
        </div>
        <Dropdown
          id="mytime-matter"
          options={[{ value: '', label: t('All matters') }, ...matterOptions]}
          value={matterFilter}
          placeholder={t('All matters')}
          onChange={(v) => setMatterFilter((Array.isArray(v) ? v[0] : v) || '')}
          width="w-48"
          thinner
        />
        <Dropdown
          id="mytime-activity"
          options={[{ value: '', label: t('All activities') }, ...activityTypes.map((a) => ({ value: String(a.id), label: t(a.label) }))]}
          value={filters.activity_type_id ? String(filters.activity_type_id) : ''}
          placeholder={t('All activities')}
          onChange={(v) => setFilters((p) => ({ ...p, activity_type_id: (Array.isArray(v) ? v[0] : v) || undefined }))}
          width="w-44"
          thinner
        />
        <Dropdown
          id="mytime-billable"
          options={[
            { value: '', label: t('All') },
            { value: 'true', label: t('Billable') },
            { value: 'false', label: t('Non-billable') },
          ]}
          value={filters.billable === undefined ? '' : String(filters.billable)}
          placeholder={t('All')}
          onChange={(v) => {
            const sel = Array.isArray(v) ? v[0] : v;
            setFilters((p) => ({ ...p, billable: sel === '' ? undefined : sel === 'true' }));
          }}
          width="w-36"
          thinner
        />

        {/* What is actually filtering, and one way out of each. */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {activeFilters.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.clear}
                className="inline-flex items-center gap-1 rounded-full border border-hairline bg-surface-sunken/70 px-2 py-0.5 text-[11px] text-fg-body transition-colors hover:bg-surface-sunken hover:text-fg-strong"
                title={t('Remove filter')}
              >
                {chip.label}
                <PiXLight className="h-3 w-3" />
              </button>
            ))}
            <Button variant="neutral" size="small" onClick={clearAllFilters}>
              {t('Clear')}
            </Button>
          </div>
        )}

        <div className="ml-auto flex items-center gap-4 text-xs text-fg-muted">
          <span>
            {t('Billable')}:{' '}
            <span className="tabular-nums text-fg-strong">{billableHours.toFixed(2)}</span>
          </span>
          <span>
            {t('Total')}:{' '}
            <span className="tabular-nums text-fg-strong">{totalHours.toFixed(2)}</span> {t('hours')}
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {error ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-10 text-center">
            {t('Could not load time entries.')}{' '}
            <button className="text-primary-600 underline" onClick={load}>{t('Retry')}</button>
          </div>
        ) : (
          <Table
            isLoading={loading && entries.length === 0}
            columns={columns}
            data={rows}
            renderCell={renderCell}
            onRowClick={openEntryPanel}
            isLoadingMore={loading}
            sortableColumns={sortableColumns}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={(nextSortBy, nextOrder) => {
              setSortBy(nextSortBy);
              setSortOrder(nextOrder);
            }}
            tableId="my-time-entries"
            min="min-h-[8rem]"
            max=""
            emptyState={{
              icon: <PiClockLight className="h-6 w-6" />,
              headline: t('No time entries'),
              subtext: t('Time you log on matters will appear here.'),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MyTimeEntriesPage;
